import './App.css';
import React, { Suspense, lazy } from 'react';
import { Button, Spin } from 'antd';
import Navigation from './Components/Navigation/Navigation';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './Components/Login/Login';
import { DashboardProvider, useDashboard } from './Contexts/DashboardContext';
import { hasSatellites } from './utils/capability';

// Screens are route-level code-split so a field device parses only the screen it
// actually opened instead of the whole app. The heavy renderers ride along with
// the screen that needs them rather than sitting in the entry bundle:
//   mapbox-gl            -> Map + Schedule/RadarSimulation
//   echarts              -> SystemReports
//   react-google-charts  -> Schedule/Timeline
// Login, Navigation and the shell stay eager: they are on the critical path
// before any route can resolve, and Login renders for unauthenticated users.
const Home = lazy(() => import('./Screens/Home/Home.js'));
const Map = lazy(() => import('./Screens/Map/Map.js'));
const Configuration = lazy(() =>
  import('./Screens/Configuration/Configuration.js'),
);
const SystemStatus = lazy(() => import('./Screens/SystemStatus/SystemStatus'));
const ScheduleTimeline = lazy(() =>
  import('./Screens/Schedule/ScheduleTimeline'),
);
const ScheduleList = lazy(() => import('./Screens/Schedule/ScheduleList'));
const ScheduleRadarSim = lazy(() =>
  import('./Screens/Schedule/ScheduleRadarSim'),
);
const SystemHistory = lazy(() =>
  import('./Screens/SystemHistory/SystemHistory'),
);

// The same loading affordance the screens use for their own data fetches, so a
// chunk fetch and a data fetch look identical to the user.
function RouteFallback() {
  return (
    <div className="center-spin">
      <Spin tip="Loading..." />
    </div>
  );
}

// Splitting the bundle means a route can now fail on the network, not just its
// data. On flaky cellular a chunk request can drop, and React.lazy surfaces that
// by throwing during render — with no boundary, that unmounts the whole app to a
// blank page. This keeps the shell up and offers a way back.
//
// It catches every render throw in the routed tree, though, not just chunk
// fetches — a bad payload that crashes a screen lands here too. So the copy
// names both possibilities instead of blaming the connection for a bug the
// user cannot fix by walking somewhere with more bars.
class RouteErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // Stable marker so these are greppable in a field device's console export.
    console.error('[route-error]', error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          className="center-spin"
          style={{ flexDirection: 'column', gap: 12, color: 'var(--muted)' }}
        >
          <span>Something went wrong loading this screen.</span>
          <span>
            This can be a connection problem or a data problem. Reload to try
            again.
          </span>
          <Button
            type="primary"
            className="touch-target"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// The route table. Separate from App because it reads tenant capability, which is only
// available below DashboardProvider.
//
// The four satellite paths are gated here rather than only in the nav: filtering a link out of
// the menu does not stop the URL from resolving when it is typed, bookmarked, or followed from
// an old link, so the redirect is the actual control and the nav filter is what keeps a dead
// link off the screen. Ungated screens stay open on every tenant - Configuration holds the only
// System Capacity field and System Reports is the pump-house graph itself.
//
// A redirect, not an omitted <Route>: with no route registered the path would fall through to
// nothing and paint a blank content area, which reads as a broken app rather than as a screen
// this tenant does not have.
function AppRoutes() {
  const { satelliteCount } = useDashboard();
  // Fails open while the count is unknown (see utils/capability), so a bookmarked satellite URL
  // can paint once before the first poll lands and then redirect. That is the same trade the nav
  // filter makes, and it is the safe direction: never strip a screen from an account whose poll
  // failed for a reason that has nothing to do with its hardware.
  const satelliteScreen = (element) =>
    hasSatellites(satelliteCount) ? element : <Navigate to="/" replace />;

  return (
    <Routes>
      <Route path="/" exact element={<Home />} />
      <Route
        path="/Schedule/Timeline"
        element={satelliteScreen(<ScheduleTimeline />)}
      />
      <Route path="/Schedule/List" element={satelliteScreen(<ScheduleList />)} />
      <Route
        path="/Schedule/RadarSimulation"
        element={satelliteScreen(<ScheduleRadarSim />)}
      />
      <Route path="/Map" element={satelliteScreen(<Map />)} />
      <Route path="/Configuration" element={<Configuration />} />
      <Route path="/SystemReports" element={<SystemStatus />} />
      <Route path="/SystemHistory" element={<SystemHistory />} />
    </Routes>
  );
}

export default function App() {
  if (
    sessionStorage.getItem('refreshToken') ||
    localStorage.getItem('refreshToken')
  ) {
    return (
      <DashboardProvider>
        <div className="app-shell">
          <header className="app-header">
            <Navigation />
          </header>
          <main className="app-content">
            <Router>
              {/* Boundary and Suspense sit inside the persistent layout, so a
                  route swap never unmounts the header or the shell. */}
              <RouteErrorBoundary>
                <Suspense fallback={<RouteFallback />}>
                  <AppRoutes />
                </Suspense>
              </RouteErrorBoundary>
            </Router>
          </main>
        </div>
      </DashboardProvider>
    );
  } else {
    return <Login />;
  }
}
